import { useEffect, useRef } from 'react';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyFeedOverrideWebApiHandler_getFeedOverrideSections,
  useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { getListingDetailsQueryKey } from './queries/properties';
import accessUtils from '../utils/accessUtils';
import { FEED_OVERRIDES_TAG } from '../constants/FeedOverrideConstants';

type UseReimportLockOptions = {
  listingAlias: string;
  enabled?: boolean;
};

const POLL_INTERVAL_MS = 5 * 1000;

export const useReimportLock = ({ listingAlias, enabled = true }: UseReimportLockOptions) => {
  const queryClient = useQueryClient();

  const { data } = useRentalManagerUserPropertyFeedOverrideWebApiHandler_getFeedOverrideSections(
    { listingAlias },
    {
      enabled: enabled && Boolean(listingAlias) && accessUtils.canAccessFeedOverrides(),
      refetchInterval: (query) => {
        const expiresAtMs = query.state.data?.reimportLockExpiresAtMs;
        return expiresAtMs && expiresAtMs > Date.now() ? POLL_INTERVAL_MS : false;
      },
      meta: {
        tags: [FEED_OVERRIDES_TAG],
        context: { api: 'rental-manager-api', details: 'useReimportLock' },
      },
    },
  );

  const expiresAtMs = data?.reimportLockExpiresAtMs;
  const isReimportLocked = Boolean(expiresAtMs && expiresAtMs > Date.now());

  const wasLockedRef = useRef(false);
  useEffect(() => {
    if (wasLockedRef.current && !isReimportLocked) {
      // The re-import settled; refetch the queries that can show reverted values.
      queryClient.invalidateQueries({ queryKey: getListingDetailsQueryKey(listingAlias) });
      queryClient.invalidateQueries({
        queryKey: useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get.getKey({ listingAlias }),
      });
    }
    wasLockedRef.current = isReimportLocked;
  }, [isReimportLocked, listingAlias, queryClient]);

  return { isReimportLocked };
};
